<script>
  import Modal from "@components/shared/modal.svelte";
  import Button from "@components/button.svelte";
  import { FileX, UserX, Search } from "lucide-svelte";
  import { get, post, patch } from "@lib/requests";
  import { cn, formatMoney, showAlert, currentUser } from "@lib/utils";
  import { onMount } from "svelte";

  let { text = "Relances", title = "Relances automatiques" } = $props()

  // True when the logged-in user is an admin or has the `invoice_manager` flag. Used to gate
  // all the management buttons below so non-managers see the modal but can't action it.
  let canManage = $derived(Boolean(currentUser()?.can_manage_invoices));

  let open = $state(false);
  let pdfOpen = $state(false);
  let pdfData = $state(null);
  let loading = $state(false);
  let loaded = $state(false);
  let error = $state(null);
  let previews = $state([]);
  let selected = $state([]);

  let emailables = $derived(previews.filter((p) => p.sendable && selected.includes(p.invoice.id)));
  let postables = $derived(previews.filter((p) => !p.sendable && selected.includes(p.invoice.id)));

  let modalWidth = $derived(previews.length > 0 ? "72rem" : "50rem");

  let allSelected = $derived(previews.length > 0 && selected.length === previews.length);
  let partSelected = $derived(previews.length > 0 && selected.length > 0 && selected.length < previews.length);

  async function loadPreviews() {
    try {
      loading = true;
      const response = await get("/api/v1/invoice_reminders/preview");
      previews = await response.json;
      // Pre-select everything by default; user can uncheck rows they want to skip
      selected = previews.map((p) => p.invoice.id);
    } catch (e) {
      error = e;
    } finally {
      loading = false;
      loaded = true;
    }
  }

  async function refetch() {
    await loadPreviews();
  }

  function openModal() {
    open = true;
    if (!loaded) loadPreviews();
  }

  function closeModal() {
    open = false;
    previews = [];
    selected = [];
    loaded = false;
  }

  function toggleAll() {
    selected = allSelected ? [] : previews.map((p) => p.invoice.id);
  }

  function toggleRow(id) {
    selected = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
  }

  async function toggleInvoiceOptOut(invoiceId, currentlyEnabled) {
    try {
      await patch("/api/v1/invoice_reminders/toggle_invoice", {
        body: { invoice_id: invoiceId, enabled: !currentlyEnabled }
      });
      await refetch();
    } catch (e) {
      showAlert(e.message, { type: "error" });
    }
  }

  async function toggleClientOptOut(clientId, currentlyEnabled) {
    if (!window.confirm("Désactiver les relances pour tous les factures de ce client ?")) return;
    try {
      await patch("/api/v1/invoice_reminders/toggle_client", {
        body: { client_id: clientId, enabled: !currentlyEnabled }
      });
      await refetch();
    } catch (e) {
      showAlert(e.message, { type: "error" });
    }
  }

  async function sendEmails() {
    if (emailables.length === 0) return;
    if (!window.confirm(`Envoyer ${emailables.length} relance(s) par email ?`)) return;
    try {
      const response = await post("/api/v1/invoice_reminders/send_selected", {
        body: { invoice_ids: emailables.map((p) => p.invoice.id) }
      });
      const data = await response.json;
      const parts = [];
      if (data.sent?.length) parts.push(`${data.sent.length} envoyée(s) par email`);
      if (data.failed?.length) parts.push(`${data.failed.length} en échec`);
      showAlert(parts.join(", ") || "Aucune relance traitée", { type: data.failed?.length ? "warning" : "success" });
      await refetch();
    } catch (e) {
      showAlert(e.message, { type: "error" });
    }
  }

  async function printSelected() {
    if (postables.length === 0) return;
    if (!window.confirm(`Imprimer ${postables.length} relance(s) (et les marquer envoyées par courrier) ?`)) return;
    try {
      const response = await post("/api/v1/invoice_reminders/print_selected", {
        body: { invoice_ids: postables.map((p) => p.invoice.id) }
      });
      const data = await response.json;
      if (response.ok && data.data) {
        pdfData = data.data;
        pdfOpen = true;
        showAlert(`${data.posted || data.count} relance(s) marquée(s) envoyée(s) par courrier`, { type: "success" });
        await refetch();
      } else {
        showAlert(data.error || "L'impression a échoué", { type: "error" });
      }
    } catch (e) {
      showAlert(e.message, { type: "error" });
    }
  }

  async function sendAndPrint() {
    if (selected.length === 0) return;
    let message = "";
    if (emailables.length && postables.length) {
      message = `Envoyer ${emailables.length} relance(s) par email et imprimer ${postables.length} (et les marquer envoyées par courrier) ?`;
    } else if (emailables.length) {
      message = `Envoyer ${emailables.length} relance(s) par email ?`;
    } else if (postables.length) {
      message = `Imprimer ${postables.length} relance(s) (et les marquer envoyées par courrier) ?`;
    }
    if (!window.confirm(message)) return;

    try {
      if (emailables.length) {
        await post("/api/v1/invoice_reminders/send_selected", {
          body: { invoice_ids: emailables.map((p) => p.invoice.id) }
        });
      }
      if (postables.length) {
        const response = await post("/api/v1/invoice_reminders/print_selected", {
          body: { invoice_ids: postables.map((p) => p.invoice.id) }
        });
        const data = await response.json;
        if (response.ok && data.data) {
          pdfData = data.data;
          pdfOpen = true;
        }
      }
      showAlert("Relances traitées", { type: "success" });
      await refetch();
    } catch (e) {
      showAlert(e.message, { type: "error" });
    }
  }

  onMount(() => {
    loadPreviews();
  });
</script>

<Button onclick={openModal}>
  <Search size={14} class="text-gray-500" />
  {text}
  {#if previews.length > 0}
    <sup class="text-xxs opacity-50 -ml-1 -mr-1">{previews.length}</sup>
  {/if}
</Button>

<Modal bind:open {title} width={modalWidth} onClose={closeModal}>
  {#if loading}
    <div class="app-message">
      <i class="fal fa-spinner fa-spin"></i>
      <p>Chargement...</p>
    </div>
  {:else if error}
    <p>Erreur: {error.message}</p>
  {:else if previews.length === 0}
    <div class="app-message">
      <i class="fal fa-info-circle"></i>
      <p>Aucune relance à envoyer aujourd'hui</p>
    </div>
  {:else}

    <!-- Information banner -->
    <div class="mb-6 p-3 bg-teal-50 border border-teal-200 rounded text-smaller text-teal-900">
      <i class="fal fa-info-circle mr-2"></i>
      Les factures envoyables par email seront automatiquement envoyées. Vous pouvez consulter les autres factures pour les imprimer manuellement.
    </div>

    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <p class="help-text">
        <span>Il y a {previews.length} facture(s) éligible(s) à une relance aujourd'hui.</span>
        {#if postables.length > 0}
          <span class="italic text-xs">(dont {postables.length} nécessitent une impression)</span>
        {/if}
      </p>
      <div class="flex items-center gap-3">
        <span class="text-xs text-gray-500">{selected.length} / {previews.length} selectionnée(s)</span>
        <input type="checkbox" class="!w-[14px] !h-[14px]" checked={allSelected} indeterminate={partSelected} onchange={toggleAll} />
      </div>
    </div>

    <!-- Table -->
    <div class="overflow-y-auto max-h-[480px]">
      <table class="table w-full shadow-md">
        <thead>
          <tr>
            <th class="w-[4%]"></th>
            <th class="w-[14%]">Référence</th>
            <th class="w-[26%]">Client</th>
            <th class="w-[14%] right">Solde</th>
            <th class="w-[8%] center">Relance</th>
            <th class="w-[10%] center">Jours</th>
            <th class="w-[12%] center">Méthode</th>
            <th class="w-[12%] center">Exclure</th>
          </tr>
        </thead>
        <tbody>
          {#each previews as p (p.invoice.id)}
            <tr class:opacity-50={!selected.includes(p.invoice.id)}>
              <td>
                <input type="checkbox" checked={selected.includes(p.invoice.id)} onclick={() => toggleRow(p.invoice.id)} />
              </td>
              <td>
                <a href={`/comptabilite/factures/${p.invoice.id}`} class="hover:underline">{p.invoice.reference}</a>
              </td>
              <td>
                <a href={`/clients/${p.client.id}`} class="hover:underline">{p.client.name}</a>
              </td>
              <td class="right">{formatMoney(p.invoice.balance)}</td>
              <td class="center">{p.sequence === 1 ? "1ère" : `${p.sequence}ème`}</td>
              <td class="center">{p.days_past_due}</td>
              <td class="center">
                <span class={cn("inline-block px-2 py-1 rounded-sm text-xs border boxed", p.sendable ? "bg-green-50 text-green-700 border-green-200" : "bg-orange-50 text-orange-700 border-orange-200")}>
                  {p.sendable ? "Email" : "Courrier"}
                </span>
              </td>
              <td class="center">
                <Button
                  naked
                  Icon={FileX}
                  iconSize={16}
                  hint={canManage ? "Exclure cette facture des relances" : "Réservé aux gestionnaires de factures"}
                  class="text-gray-500 hover:text-red-600 mr-3 inline-flex"
                  disabled={!canManage}
                  onclick={() => toggleInvoiceOptOut(p.invoice.id, p.invoice.reminders_enabled)}
                />
                <Button
                  naked
                  Icon={UserX}
                  iconSize={16}
                  hint={canManage ? "Exclure ce client (toutes ses factures)" : "Réservé aux gestionnaires de factures"}
                  class="text-gray-500 hover:text-red-600 inline-flex"
                  disabled={!canManage}
                  onclick={() => toggleClientOptOut(p.client.id, p.client.invoice_reminders_enabled)}
                />
              </td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}

  {#snippet footerButtons()}
    {#if previews.length > 0}
      <button class="button" disabled={loading || postables.length === 0 || !canManage} title={canManage ? null : "Réservé aux gestionnaires de factures"} onclick={printSelected}>
        Imprimer maintenant
        {#if postables.length > 0}<sup class="text-xxs opacity-50 -ml-1 -mr-1">{postables.length}</sup>{/if}
        <i class="fal fa-print"></i>
      </button>
      <button class="button" disabled={loading || emailables.length === 0 || !canManage} title={canManage ? null : "Réservé aux gestionnaires de factures"} onclick={sendEmails}>
        Envoyer maintenant
        {#if emailables.length > 0}<sup class="text-xxs opacity-50 -ml-1 -mr-1">{emailables.length}</sup>{/if}
        <i class="fal fa-envelope"></i>
      </button>
      <button class="button outline-submit" disabled={loading || selected.length === 0 || !canManage} title={canManage ? null : "Réservé aux gestionnaires de factures"} onclick={sendAndPrint}>
        Envoyer et imprimer maintenant
        {#if selected.length > 0}<sup class="text-xxs opacity-50 -ml-1 -mr-1">{selected.length}</sup>{/if}
        <i class="fal fa-paper-plane"></i>
      </button>
    {/if}
  {/snippet}
</Modal>

{#if pdfData}
  <Modal bind:open={pdfOpen} title="Aperçu PDF" width="70rem" height="80vh" onClose={() => (pdfOpen = false)}>
    <object title="Aperçu PDF" type="application/pdf" data={`data:application/pdf;base64,${pdfData}`} width="100%" height="700px"></object>
  </Modal>
{/if}
